import type {
  BudgetTeacherPartialInfoDto,
  BudgetTeacherPartialRowDto,
  BudgetTeacherPickListItemDto,
} from '@/features/reports/api/budgetTeacherPartialInfo.dto';

export function teacherRowsFromBudgetPartial(
  dto: BudgetTeacherPartialInfoDto | null | undefined
): BudgetTeacherPartialRowDto[] {
  if (!dto) return [];
  return dto.cohortTeachers;
}

export function budgetPickFirstTitle(items: BudgetTeacherPickListItemDto[], pickListCode: string): string {
  const m = items.find((i) => i.pickListCode === pickListCode);
  const t = m?.title?.trim();
  return t && t.length > 0 ? t : '—';
}

/** Join every `TeacherEducationLevel` item (multi-select legacy form). */
export function budgetPickEducationTitles(items: BudgetTeacherPickListItemDto[]): string {
  const titles = items
    .filter((i) => i.pickListCode === 'TeacherEducationLevel')
    .map((i) => i.title?.trim())
    .filter((t): t is string => Boolean(t && t.length > 0));
  return titles.length > 0 ? titles.join(', ') : '—';
}

export function fmtBudgetDdMmYyyy(iso: string | null | undefined): string {
  if (!iso || !String(iso).trim()) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function fmtBudgetLumpSum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

/** API `TotalSalaryLL` — grouped integer, no currency symbol. */
export function fmtBudgetTotalSalaryLl(n: number | null | undefined): string {
  return fmtBudgetLumpSum(n);
}

/** API `TotalSalaryUSD` — whole USD. */
export function fmtBudgetTotalSalaryUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

const PICK_TEACHER_ROLE = 'TeacherRole';
const PICK_TEACHER_QUALIFICATION = 'TeacherQualification';

/**
 * Mirrors national cohort filters: empty `roleIds` or `qualificationIds` means no filter on that axis.
 * Matches `pickListItemId` on budget teacher pick-list rows (same codes as the equity pick lists).
 */
export function filterBudgetTeachersByAppliedPickLists(
  rows: BudgetTeacherPartialRowDto[],
  roleIds: string[],
  qualificationIds: string[]
): BudgetTeacherPartialRowDto[] {
  const roleSet = new Set(roleIds.map((id) => id.trim()).filter(Boolean));
  const qualSet = new Set(qualificationIds.map((id) => id.trim()).filter(Boolean));
  const filterRoles = roleSet.size > 0;
  const filterQuals = qualSet.size > 0;
  if (!filterRoles && !filterQuals) return rows;

  return rows.filter((row) => {
    const items = row.budgetTeacherPickListItems ?? [];
    if (filterRoles) {
      const roleItem = items.find((i) => i.pickListCode === PICK_TEACHER_ROLE);
      const rid = roleItem?.pickListItemId?.trim();
      if (!rid || !roleSet.has(rid)) return false;
    }
    if (filterQuals) {
      const qualIds = items
        .filter((i) => i.pickListCode === PICK_TEACHER_QUALIFICATION)
        .map((i) => i.pickListItemId?.trim())
        .filter((id): id is string => Boolean(id));
      if (qualIds.length === 0 || !qualIds.some((id) => qualSet.has(id))) return false;
    }
    return true;
  });
}

/**
 * Total salary in USD: `sum(basicSalary + socialAssistanceLL + socialAssistanceUSD * exchangeRateAmount) / exchangeRateAmount`.
 * Missing numeric parts count as 0. Returns `null` if exchange rate is missing or not usable.
 */
export function sumBudgetTeacherSalaryTotalUsd(
  rows: BudgetTeacherPartialRowDto[],
  exchangeRateAmount: number | null | undefined
): number | null {
  if (exchangeRateAmount == null || !Number.isFinite(exchangeRateAmount) || exchangeRateAmount === 0) return null;
  const ex = exchangeRateAmount;
  let sumLl = 0;
  for (const r of rows) {
    const b = r.basicSalary != null && Number.isFinite(r.basicSalary) ? r.basicSalary : 0;
    const sll = r.socialAssistanceLL != null && Number.isFinite(r.socialAssistanceLL) ? r.socialAssistanceLL : 0;
    const usd = r.socialAssistanceUSD != null && Number.isFinite(r.socialAssistanceUSD) ? r.socialAssistanceUSD : 0;
    sumLl += b + sll + usd * ex;
  }
  return sumLl / ex;
}
